import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import type { AppleSignInResult, AuthError, AuthPort } from "./types";
import { registerAppUrlRoute } from "./links";
import { sha256Hex } from "./sha256";

const APPLE_AUTHORIZE = "https://appleid.apple.com/auth/authorize";
const APPLE_RETURN_PATH = "/apple"; // hamlak://auth/apple (dev 앱은 hamlak-dev://, AC0-155)

const servicesId = () => import.meta.env.VITE_APPLE_SERVICES_ID;
const redirectUri = () => import.meta.env.VITE_APPLE_REDIRECT_URI;
const googleWebClientId = () => import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID;
const googleIosClientId = () => import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID;
const isIos = () => Capacitor.getPlatform() === "ios";

type GoogleInit = NonNullable<Parameters<typeof SocialLogin.initialize>[0]["google"]>;

/**
 * 플랫폼별 Google 초기화 값. 없으면 null(unavailable).
 * - Android: webClientId(Credential Manager 의 서버 클라이언트 = 토큰 aud).
 * - iOS: iOSClientId(GoogleSignIn, 토큰 aud = iOS 클라이언트 → 서버 GOOGLE_OAUTH_CLIENT_IDS 에 포함, ios.md A0).
 *   iOSServerClientId 는 넘기지 않는다 — offline 모드(serverAuthCode) 전용이고 우리는 online 의 idToken 만 쓴다.
 *   Info.plist 의 역순 클라이언트 ID URL 스킴은 ios/App/Config/<Stage>.xcconfig 의 HAMLAK_GOOGLE_REVERSED_CLIENT_ID.
 */
function googleInit(): GoogleInit | null {
  if (isIos()) {
    const iOSClientId = googleIosClientId();
    return iOSClientId ? { iOSClientId } : null;
  }
  const webClientId = googleWebClientId();
  return webClientId ? { webClientId } : null;
}

// SocialLogin.initialize는 제공자마다 앱 수명 동안 1회(넘긴 제공자만 초기화하고 다른 제공자는 건드리지 않는다).
// 첫 로그인 시도 때 지연 초기화하고 실패하면 다음 시도가 다시 한다.
let googleReady: Promise<void> | null = null;
function ensureGoogle(google: GoogleInit): Promise<void> {
  googleReady ??= SocialLogin.initialize({ google }).catch((e: unknown) => {
    googleReady = null;
    throw e;
  });
  return googleReady;
}

// iOS Apple: 리다이렉트 없음(redirectUrl 생략 → 플러그인이 서버로 보내지 않는다). useProperTokenExchange=true 여야 authorizationCode 가 결과에 실린다.
let appleReady: Promise<void> | null = null;
function ensureAppleIos(): Promise<void> {
  appleReady ??= SocialLogin.initialize({ apple: { useProperTokenExchange: true } }).catch((e: unknown) => {
    appleReady = null;
    throw e;
  });
  return appleReady;
}

/** 테스트 전용: 초기화 메모를 지운다. */
export function resetGoogleForTest(): void {
  googleReady = null;
  appleReady = null;
}

const messageOf = (e: unknown): string =>
  e instanceof Error ? e.message : typeof e === "object" && e !== null && "message" in e ? String((e as { message: unknown }).message) : String(e);

/**
 * 취소는 플러그인이 메시지만 주는 reject로 온다.
 * Android Credential Manager "[16] Cancelled by user." · iOS GoogleSignIn "The user canceled the sign-in flow." ·
 * iOS Apple ASAuthorizationError.canceled = "…AuthorizationError error 1001.)"
 */
const isCancelMessage = (e: unknown): boolean => /cancel|AuthorizationError error 1001\b/i.test(messageOf(e));

// 호출당 하나의 대기. 사용자가 돌아오지 않은 채 다시 누르면 이전 대기를 failed로 거부한다(타임아웃 없음).
let pendingApple: { reject: (e: AuthError) => void } | null = null;

export function buildAppleAuthorizeUrl(p: { clientId: string; redirectUri: string; state: string; nonce: string }): string {
  const sp = new URLSearchParams({
    response_type: "code id_token",
    response_mode: "form_post",
    client_id: p.clientId,
    redirect_uri: p.redirectUri,
    state: p.state,
    nonce: p.nonce,
    scope: "email",
  });
  return `${APPLE_AUTHORIZE}?${sp.toString()}`;
}

/**
 * 네이티브 로그인 어댑터.
 * - Google(2.1): @capgo/capacitor-social-login → Android Credential Manager · iOS GoogleSignIn. initialize({google}) 1회 → login({provider:'google'}) → idToken.
 *   email·profile·openid는 플러그인 기본 scope라 options.scopes를 넘기지 않는다(넘기면 MainActivity 수정을 요구한다). 취소 → cancelled, ID 없음 → unavailable.
 * - Apple iOS(ios.md A4): 플러그인 apple 공급자 = 네이티브 ASAuthorizationController. nonce 는 Android 와 같다(Apple 에 sha256, 서버에 원문).
 *   authorizationCode 는 결과에 담아 돌려주고 호출부가 POST /auth/apple 에 싣는다(서버가 교환해 두고 탈퇴 때 revoke, AC0-269).
 * - Apple Android(auth-session 3.8): Custom Tabs로 Apple authorize → 서버 /api/auth/apple/callback(form_post) → 302 hamlak(-dev)://auth/apple?id_token&code&state
 *   → appUrlOpen 수신 → state 대조 → { idToken, nonce, authorizationCode? }. 검증·세션 발급은 호출부가 POST /auth/apple(Bearer)로.
 *   nonce: Apple에는 sha256(원문)을 보내고 서버에는 원문을 보낸다. hamlak:// 커스텀 스킴을 다른 앱이 가로채 id_token을 얻어도
 *   토큰에는 해시만 있어 원문을 모르면 POST /auth/apple 이 401 이다.
 */
export const nativeAuth: AuthPort = {
  async signInWithGoogle() {
    const google = googleInit();
    if (!google) throw { code: "unavailable" } satisfies AuthError;
    try {
      await ensureGoogle(google);
    } catch (cause) {
      throw { code: "unavailable", cause } satisfies AuthError;
    }
    let result: Awaited<ReturnType<typeof SocialLogin.login<"google">>>["result"];
    try {
      ({ result } = await SocialLogin.login({ provider: "google", options: {} }));
    } catch (cause) {
      if (isCancelMessage(cause)) throw { code: "cancelled", cause } satisfies AuthError;
      throw { code: "failed", cause } satisfies AuthError;
    }
    if (result.responseType !== "online" || !result.idToken) throw { code: "failed", cause: "missing_id_token" } satisfies AuthError;
    return { idToken: result.idToken };
  },
  async signInWithApple() {
    return isIos() ? signInWithAppleIos() : signInWithAppleWeb();
  },
  async signOut() {
    // Google: Credential Manager·GoogleSignIn 상태 정리(초기화 전이면 할 것이 없다). Apple은 서버 세션 외 로컬 상태가 없다.
    if (!googleReady) return;
    try {
      await SocialLogin.logout({ provider: "google" });
    } catch {
      // 로그아웃 실패는 세션 폐기를 막지 않는다.
    }
  },
};

async function signInWithAppleIos(): Promise<AppleSignInResult> {
  try {
    await ensureAppleIos();
  } catch (cause) {
    throw { code: "unavailable", cause } satisfies AuthError;
  }
  const nonce = crypto.randomUUID();
  let result: Awaited<ReturnType<typeof SocialLogin.login<"apple">>>["result"];
  try {
    // 이름은 받지 않는다(닉네임 자동 생성, AppleAuthRequest.user deprecated) — 웹 OAuth 의 scope=email 과 같다
    ({ result } = await SocialLogin.login({ provider: "apple", options: { scopes: ["email"], nonce: await sha256Hex(nonce) } }));
  } catch (cause) {
    if (isCancelMessage(cause)) throw { code: "cancelled", cause } satisfies AuthError;
    throw { code: "failed", cause } satisfies AuthError;
  }
  if (!result.idToken) throw { code: "failed", cause: "missing_id_token" } satisfies AuthError;
  return { idToken: result.idToken, nonce, ...(result.authorizationCode ? { authorizationCode: result.authorizationCode } : {}) };
}

async function signInWithAppleWeb(): Promise<AppleSignInResult> {
  const clientId = servicesId();
  const redirect = redirectUri();
  if (!clientId || !redirect) throw { code: "unavailable" } satisfies AuthError;

  // 이전 시도는 사용자가 Custom Tabs 를 끝내지 않고 닫은 것(닫힘을 알 길이 없다). 새 시도가 대체하므로 조용히 끝낸다 — failed 면 새 화면과 함께 오류 토스트가 뜬다(AC0-148)
  pendingApple?.reject({ code: "cancelled", cause: "superseded" });
  pendingApple = null;

  const state = crypto.randomUUID();
  const nonce = crypto.randomUUID();
  const nonceHash = await sha256Hex(nonce);

  let abort: (e: AuthError) => void = () => undefined;
  const result = new Promise<AppleSignInResult>((resolve, reject) => {
    const unregister = registerAppUrlRoute("auth", (url) => {
      if (url.pathname !== APPLE_RETURN_PATH) return false;
      finish();
      void Browser.close().catch(() => undefined);
      const q = url.searchParams;
      const error = q.get("error");
      if (error) {
        reject({ code: error === "user_cancelled_authorize" ? "cancelled" : "failed", cause: error } satisfies AuthError);
        return true;
      }
      if (q.get("state") !== state) {
        reject({ code: "failed", cause: "state_mismatch" } satisfies AuthError);
        return true;
      }
      const idToken = q.get("id_token");
      if (!idToken) {
        reject({ code: "failed", cause: "missing_id_token" } satisfies AuthError);
        return true;
      }
      const code = q.get("code"); // AC0-269: 서버 콜백이 Apple form_post 의 code 를 그대로 넘긴다
      resolve({ idToken, nonce, ...(code ? { authorizationCode: code } : {}) });
      return true;
    });
    const finish = () => {
      unregister();
      pendingApple = null;
    };
    abort = (e) => {
      finish();
      reject(e);
    };
    pendingApple = { reject: abort };
  });

  try {
    await Browser.open({ url: buildAppleAuthorizeUrl({ clientId, redirectUri: redirect, state, nonce: nonceHash }) });
  } catch (cause) {
    abort({ code: "unavailable", cause });
  }
  return result;
}
